/**
 * Compre junto (antigo "Shop the look") - Vanilla JS module
 * Namespace: window.ShopTheLook
 *
 * Sibling feature to assets/kit-new.js: same metafield-driven, per-product
 * variant resolution and the same <xo-cart-add> multi-add delegation, but
 * all items are shown side by side (no step wizard) and each item can be
 * toggled out of the purchase via a checkbox.
 *
 * Next Sport:
 * - O botao "Comprar junto" fica sempre ativo. Se faltar escolher o tamanho
 *   de algum produto marcado, o clique destaca esse produto e mostra o aviso.
 * - Tamanhos esgotados aparecem riscados e nao podem ser escolhidos.
 * - Depois de adicionar, o botao volta a funcionar.
 * - Total: soma dos precos "de" (preco comparativo; se o produto nao tiver,
 *   usa o proprio preco) riscada, o valor final e a % de desconto.
 * - Parcelamento no formato "3x de R$ x,xx sem juros" com destaque em negrito.
 */

(function () {
  'use strict';

  const ShopTheLook = {
    state: {
      selections: {}, // { productId: { variantId, price, compare, color, size, otherOptions, included } }
      productsData: {}, // { productId: { variants, options, title, featuredImage } }
      order: [], // productIds in display order
    },

    init() {
      const containers = document.querySelectorAll('.stl');
      containers.forEach((container) => this.initOne(container));
    },

    initOne(container) {
      if (container.dataset.stlBound === 'true') return;
      container.dataset.stlBound = 'true';

      const refs = {
        container,
        items: container.querySelector('[data-stl-items]'),
        cta: container.querySelector('[data-stl-cta]'),
        ctaText: container.querySelector('[data-cta-text]'),
        loading: container.querySelector('[data-stl-loading]'),
        errorEl: container.querySelector('[data-stl-error]'),
        hintEl: container.querySelector('[data-stl-hint]'),
        cartAddEl: container.querySelector('[data-stl-cart-add]'),
        thumbnails: container.querySelector('[data-stl-thumbnails]'),
        totalPriceEl: container.querySelector('[data-stl-total-price]'),
        totalCompareEl: container.querySelector('[data-stl-total-compare]'),
        totalOffEl: container.querySelector('[data-stl-total-off]'),
        totalInstallmentEl: container.querySelector('[data-stl-total-installment]'),
        installments: parseInt(container.dataset.stlInstallments, 10) || 0,
      };

      this.loadProductsData(container);
      this.initSelections(container);
      this.markUnavailableSizes(container);
      this.bindEvents(refs);
      this.renderThumbnails(refs);
      this.updateTotal(refs);
      this.updateCta(refs);
    },

    loadProductsData(container) {
      const scripts = container.querySelectorAll('script[data-product-variants]');
      scripts.forEach((script) => {
        try {
          const data = JSON.parse(script.textContent);
          this.state.productsData[data.productId] = data;
        } catch (e) {
          console.error('ShopTheLook: failed to parse product data', e);
        }
      });
    },

    initSelections(container) {
      const items = container.querySelectorAll('[data-stl-item]');
      items.forEach((item) => {
        const productId = parseInt(item.dataset.productId, 10);
        const productData = this.state.productsData[productId];
        const firstVariant = productData ? productData.variants[0] : null;
        this.state.order.push(productId);
        this.state.selections[productId] = {
          variantId: null,
          price: firstVariant ? firstVariant.price : 0,
          compare: firstVariant ? firstVariant.compare_at_price || 0 : 0,
          // Color has no picker in this widget (size-only, by design), so
          // default it to the first variant's color so resolveVariant() still
          // has a complete set of options to match against once a size is
          // picked. A multi-color product is locked to that first color.
          color: this.getDefaultOptionValue(productData, ['cor', 'color']),
          size: null,
          otherOptions: {},
          included: true,
        };
      });
    },

    // Tamanhos sem estoque (para a cor usada aqui) ficam riscados e
    // desativados, para o cliente nao escolher algo que nao pode comprar.
    markUnavailableSizes(container) {
      container.querySelectorAll('[data-stl-item]').forEach((item) => {
        const productId = parseInt(item.dataset.productId, 10);
        const productData = this.state.productsData[productId];
        const selection = this.state.selections[productId];
        if (!productData) return;

        const colorIndex = productData.options.findIndex((name) => /cor|color/i.test(name));
        const sizeIndex = productData.options.findIndex((name) => /tamanho|size/i.test(name));
        if (sizeIndex === -1) return;

        item.querySelectorAll('[data-size]').forEach((btn) => {
          const size = btn.dataset.size;
          const available = productData.variants.some((variant) =>
            variant.available &&
            variant.options[sizeIndex] === size &&
            (colorIndex === -1 || !selection.color || variant.options[colorIndex] === selection.color)
          );
          if (!available) {
            btn.disabled = true;
            btn.setAttribute('aria-label', 'Tamanho ' + size + ' esgotado');
          }
        });
      });
    },

    getDefaultOptionValue(productData, nameFragments) {
      if (!productData || !productData.options || !productData.variants[0]) return null;
      const index = productData.options.findIndex((name) =>
        nameFragments.some((fragment) => name.toLowerCase().includes(fragment))
      );
      if (index === -1) return null;
      return productData.variants[0].options[index] || null;
    },

    bindEvents(refs) {
      refs.items.addEventListener('click', (e) => {
        const sizeBtn = e.target.closest('[data-size]');
        if (sizeBtn) {
          e.preventDefault();
          this.handleSizeClick(refs, sizeBtn);
          return;
        }
      });

      refs.items.addEventListener('change', (e) => {
        const checkbox = e.target.closest('[data-stl-include]');
        if (checkbox) {
          this.handleIncludeToggle(refs, checkbox);
        }
      });

      refs.cta.addEventListener('click', (e) => {
        e.preventDefault();
        if (refs.cta.disabled) return;
        if (!this.isAllReady()) {
          this.highlightMissing(refs);
          return;
        }
        this.addAllToCart(refs);
      });
    },

    handleSizeClick(refs, sizeBtn) {
      if (sizeBtn.disabled) return;

      const item = sizeBtn.closest('[data-stl-item]');
      const productId = parseInt(item.dataset.productId, 10);
      const size = sizeBtn.dataset.size;

      this.state.selections[productId].size = size;
      item.querySelectorAll('[data-size]').forEach((sz) => {
        const isSelected = sz.dataset.size === size;
        sz.classList.toggle('is-selected', isSelected);
        sz.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      });

      this.resolveVariant(productId);
      if (this.isItemReady(productId)) item.classList.remove('is-missing');
      this.updateTotal(refs);
      this.updateCta(refs);
    },

    handleIncludeToggle(refs, checkbox) {
      const item = checkbox.closest('[data-stl-item]');
      const productId = parseInt(item.dataset.productId, 10);
      this.state.selections[productId].included = checkbox.checked;
      item.classList.toggle('is-excluded', !checkbox.checked);
      if (!checkbox.checked) item.classList.remove('is-missing');

      this.renderThumbnails(refs);
      this.updateTotal(refs);
      this.updateCta(refs);
    },

    resolveVariant(productId) {
      const selection = this.state.selections[productId];
      const productData = this.state.productsData[productId];
      if (!productData) return;

      const selectedOptions = [];
      productData.options.forEach((optionName, index) => {
        const lowerName = optionName.toLowerCase();
        if (lowerName.includes('cor') || lowerName.includes('color')) {
          selectedOptions[index] = selection.color;
        } else if (lowerName.includes('tamanho') || lowerName.includes('size')) {
          selectedOptions[index] = selection.size;
        } else {
          selectedOptions[index] = selection.otherOptions[optionName] || null;
        }
      });

      if (selectedOptions.includes(null)) {
        selection.variantId = null;
        return;
      }

      const matchingVariant = productData.variants.find((variant) =>
        variant.options.every((opt, idx) => opt === selectedOptions[idx])
      );

      if (matchingVariant && matchingVariant.available) {
        selection.variantId = matchingVariant.id;
        selection.price = matchingVariant.price;
        selection.compare = matchingVariant.compare_at_price || 0;
      } else {
        selection.variantId = null;
      }
    },

    isItemReady(productId) {
      const selection = this.state.selections[productId];
      if (!selection.included) return true;
      return selection.variantId !== null;
    },

    hasIncluded() {
      return this.state.order.some((id) => this.state.selections[id].included);
    },

    isAllReady() {
      return this.hasIncluded() && this.state.order.every((id) => this.isItemReady(id));
    },

    missingIds() {
      return this.state.order.filter((id) => !this.isItemReady(id));
    },

    // Destaca os produtos marcados que ainda estao sem tamanho, leva a tela
    // ate o primeiro e explica o que falta.
    highlightMissing(refs) {
      const missing = this.missingIds();
      let first = null;
      refs.items.querySelectorAll('[data-stl-item]').forEach((item) => {
        const isMissing = missing.includes(parseInt(item.dataset.productId, 10));
        item.classList.toggle('is-missing', isMissing);
        if (isMissing && !first) first = item;
      });
      if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.showError(
        refs,
        missing.length === 1
          ? 'Escolha o tamanho do produto destacado para comprar junto.'
          : 'Escolha o tamanho dos ' + missing.length + ' produtos destacados para comprar junto.'
      );
    },

    installmentHtml(total, installments) {
      const perInstallment = Math.round(total / installments);
      return '<strong>' + installments + 'x</strong> de <strong>' + this.formatMoney(perInstallment) + '</strong> sem juros';
    },

    // Total: soma dos precos finais e soma dos precos "de" (preco
    // comparativo de cada produto; quem nao tem entra com o proprio preco).
    updateTotal(refs) {
      const totals = this.state.order.reduce((acc, id) => {
        const selection = this.state.selections[id];
        if (!selection.included) return acc;
        const price = Number(selection.price || 0);
        const compare = Math.max(Number(selection.compare || 0), price);
        acc.price += price;
        acc.compare += compare;
        return acc;
      }, { price: 0, compare: 0 });

      if (refs.totalPriceEl) {
        refs.totalPriceEl.textContent = this.formatMoney(totals.price);
      }

      const hasDiscount = totals.compare > totals.price && totals.price > 0;
      const percentOff = hasDiscount ? Math.round((1 - totals.price / totals.compare) * 100) : 0;

      if (refs.totalCompareEl) {
        refs.totalCompareEl.textContent = hasDiscount ? this.formatMoney(totals.compare) : '';
        refs.totalCompareEl.hidden = !hasDiscount;
      }

      if (refs.totalOffEl) {
        refs.totalOffEl.textContent = percentOff > 0 ? '-' + percentOff + '%' : '';
        refs.totalOffEl.hidden = !(percentOff > 0);
      }

      if (refs.totalInstallmentEl && refs.installments > 1) {
        refs.totalInstallmentEl.innerHTML = this.installmentHtml(totals.price, refs.installments);
      }
    },

    renderThumbnails(refs) {
      if (!refs.thumbnails) return;
      refs.thumbnails.innerHTML = '';
      this.state.order.forEach((id) => {
        if (!this.state.selections[id].included) return;
        const data = this.state.productsData[id];
        if (!data || !data.featuredImage) return;
        const img = document.createElement('img');
        img.src = data.featuredImage;
        img.alt = data.title;
        img.loading = 'lazy';
        img.className = 'stl__thumbnail';
        refs.thumbnails.appendChild(img);
      });
    },

    // O botao so fica desativado se nenhum produto estiver marcado. Sem
    // tamanho escolhido ele continua clicavel e explica o que falta.
    updateCta(refs) {
      refs.cta.disabled = !this.hasIncluded();
      refs.cta.classList.toggle('is-pending', !this.isAllReady());

      if (refs.hintEl) {
        const missing = this.missingIds().length;
        if (!this.hasIncluded()) {
          refs.hintEl.textContent = 'Marque ao menos um produto para comprar junto.';
          refs.hintEl.hidden = false;
        } else if (missing > 0) {
          refs.hintEl.textContent = missing === 1
            ? 'Falta escolher o tamanho de 1 produto.'
            : 'Falta escolher o tamanho de ' + missing + ' produtos.';
          refs.hintEl.hidden = false;
        } else {
          refs.hintEl.hidden = true;
        }
      }
      if (this.isAllReady() && refs.errorEl) refs.errorEl.hidden = true;
    },

    formatMoney(cents) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100);
    },

    addAllToCart(refs) {
      const variantIds = [];
      this.state.order.forEach((id) => {
        const selection = this.state.selections[id];
        if (selection.included && selection.variantId) {
          variantIds.push(selection.variantId);
        }
      });

      if (variantIds.length === 0) {
        this.showError(refs, 'Selecione ao menos um produto.');
        return;
      }

      if (!refs.cartAddEl || typeof window.cartSubscribe !== 'function') {
        console.error('ShopTheLook: native cart-add trigger unavailable');
        this.showError(refs, 'Erro ao adicionar ao carrinho. Tente novamente.');
        return;
      }

      refs.cta.disabled = true;
      refs.ctaText.hidden = true;
      refs.loading.hidden = false;
      refs.errorEl.hidden = true;

      const idsJson = JSON.stringify(variantIds);
      refs.cartAddEl.setAttribute('xo-variant-id', idsJson);
      refs.cartAddEl.setAttribute('xo-product-id', idsJson);

      let sawLoading = false;
      const unsubscribe = window.cartSubscribe((cartState) => {
        if (cartState.addIdLoading) {
          sawLoading = true;
          return;
        }
        if (!sawLoading) return;

        unsubscribe();
        refs.ctaText.hidden = false;
        refs.loading.hidden = true;
        refs.cta.disabled = false;

        if (cartState.addErrorMessage) {
          this.showError(refs, cartState.addErrorMessage);
        }
      });

      refs.cartAddEl.click();
    },

    showError(refs, message) {
      refs.errorEl.textContent = message;
      refs.errorEl.hidden = false;
      clearTimeout(this.errorTimer);
      this.errorTimer = setTimeout(() => {
        refs.errorEl.hidden = true;
      }, 6000);
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => ShopTheLook.init());
  } else {
    ShopTheLook.init();
  }

  window.ShopTheLook = ShopTheLook;
})();
